"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Amy panel — 2026-08-30 owner redesign: the old modal drawer BLOCKED the
 * page ("it hides my daily target and I constantly have to close it").
 * Amy is now a NON-modal, draggable sheet:
 *
 *  - three snap states: full (big comfortable chat), half (chat + the
 *    dashboard visible above), minimized (a slim pill bar — conversation
 *    kept warm, page fully visible);
 *  - drag the handle (or tap it) to move between states — check a number
 *    on Today, pull Amy back up, continue the sentence;
 *  - the page behind stays scrollable and clickable at all times — no
 *    overlay, no focus trap;
 *  - the chat stays MOUNTED across snaps, so streaming replies and the
 *    composer draft survive every resize.
 *
 * Desktop: a right-anchored column (max-w-md). Mobile web: full width.
 */

import { useEffect, useState } from "react"
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from "framer-motion"
import { ChevronDown, ChevronUp, Minus, UnfoldHorizontal, FoldHorizontal, X } from "lucide-react"
import { useI18n } from "@/i18n"
import { notifyAmyDataChanged } from "@/lib/amySync"
import { trackFunnelEvent } from "@/lib/analytics"
import { AmyChat } from "./AmyChat"

interface AmyDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** ADR-027: inserted into the composer on open, never auto-sent — see lib/amyLauncher.ts. */
  initialPrompt?: string | null
}

type SnapState = "full" | "half" | "min"

const SNAP_HEIGHT: Record<SnapState, string> = {
  full: "min(88vh, 900px)",
  half: "min(48vh, 520px)",
  min: "3.5rem",
}

export function AmyDrawer({ open, onOpenChange, initialPrompt }: AmyDrawerProps) {
  const { t } = useI18n()
  const reduce = useReducedMotion()
  const [snap, setSnap] = useState<SnapState>("full")
  // Owner 2026-08-29: drag starts ONLY from the handle bar — with the whole
  // sheet draggable, trying to SELECT chat text started a drag instead, so
  // nothing in the conversation could be copied.
  const dragControls = useDragControls()
  // Owner 2026-08-29: desktop width toggle — tables and plan diffs breathe
  // in wide mode; everything else (snaps, drag, mounted chat) unchanged.
  const [wide, setWide] = useState(false)

  // Reopening always lands on a usable size, never the minimized bar.
  useEffect(() => {
    if (open) {
      setSnap("full")
      trackFunnelEvent("amy_opened")
    }
    // Closing the sheet is the natural "back to the dashboard" moment —
    // refetch as a belt on top of the per-tool broadcasts (ADR-036).
    else notifyAmyDataChanged()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onOpenChange])

  function handleDragEnd(_: unknown, info: PanInfo) {
    const dy = info.offset.y + info.velocity.y * 0.15
    if (dy > 60) {
      setSnap(prev => (prev === "full" ? "half" : "min"))
    } else if (dy < -60) {
      setSnap(prev => (prev === "min" ? "half" : "full"))
    }
  }

  function cycleDown() {
    setSnap(prev => (prev === "full" ? "half" : "min"))
  }
  function cycleUp() {
    setSnap(prev => (prev === "min" ? "half" : "full"))
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.section
          role="complementary"
          aria-label={t("amy.title")}
          initial={reduce ? false : { y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1, height: SNAP_HEIGHT[snap] }}
          exit={reduce ? undefined : { y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 36 }}
          drag="y"
          dragControls={dragControls}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.08}
          onDragEnd={handleDragEnd}
          className={`fixed right-0 bottom-0 left-0 z-50 flex flex-col overflow-hidden rounded-t-bb-xl border border-b-0 border-line bg-paper shadow-[0_-8px_40px_rgba(23,53,43,0.18)] sm:left-auto sm:rounded-tr-none sm:border-r-0 sm:transition-[width] sm:duration-300 sm:ease-out ${
            wide ? "sm:w-[min(46rem,92vw)]" : "sm:w-[26.5rem]"
          }`}
        >
          {/* ------------------------------------------------ Handle bar */}
          <div
            className="flex shrink-0 cursor-grab touch-none items-center justify-between gap-2 border-b border-line px-4 py-2.5 select-none active:cursor-grabbing"
            onPointerDown={e => dragControls.start(e)}
            onClick={() => (snap === "min" ? setSnap("half") : undefined)}
          >
            <div className="flex items-center gap-2">
              <span className="flex size-7 items-center justify-center rounded-full bg-green">
                <span aria-hidden="true" className="text-xs">
                  ✨
                </span>
              </span>
              <span className="font-semibold text-ink">{t("amy.title")}</span>
            </div>
            {/* Visible drag affordance */}
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-1.5 h-1 w-10 -translate-x-1/2 rounded-full bg-line"
            />
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                aria-label={wide ? t("amy.panel.narrow") : t("amy.panel.widen")}
                title={wide ? t("amy.panel.narrow") : t("amy.panel.widen")}
                onClick={() => setWide(w => !w)}
                className="hidden rounded-bb-sm p-2 text-ink-2 transition-colors hover:bg-cream hover:text-ink sm:block"
              >
                {wide ? (
                  <FoldHorizontal className="size-4" aria-hidden="true" />
                ) : (
                  <UnfoldHorizontal className="size-4" aria-hidden="true" />
                )}
              </button>
              {snap !== "full" && (
                <button
                  type="button"
                  aria-label={t("amy.panel.expand")}
                  onClick={cycleUp}
                  className="rounded-bb-sm p-2 text-ink-2 transition-colors hover:bg-cream hover:text-ink"
                >
                  <ChevronUp className="size-4" aria-hidden="true" />
                </button>
              )}
              {snap !== "min" && (
                <button
                  type="button"
                  aria-label={t("amy.panel.collapse")}
                  onClick={cycleDown}
                  className="rounded-bb-sm p-2 text-ink-2 transition-colors hover:bg-cream hover:text-ink"
                >
                  {snap === "half" ? (
                    <Minus className="size-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="size-4" aria-hidden="true" />
                  )}
                </button>
              )}
              <button
                type="button"
                aria-label={t("amy.panel.close")}
                onClick={() => onOpenChange(false)}
                className="rounded-bb-sm p-2 text-ink-2 transition-colors hover:bg-cream hover:text-ink"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Chat stays mounted in every snap — hidden, not unmounted, when
              minimized, so drafts and streaming replies survive. */}
          <div className={`min-h-0 flex-1 ${snap === "min" ? "hidden" : "flex flex-col"}`}>
            <AmyChat initialPrompt={initialPrompt} />
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  )
}
